import "./index.css";
import React, { useState, useEffect } from "react";
import { createClient } from "@supabase/supabase-js";
import { Auth } from "@supabase/auth-ui-react";
import { ThemeSupa } from "@supabase/auth-ui-shared";
import { Chat } from "./components/Chat";
import { ChatProvider } from "./contexts/ChatContext";
import { AuthProvider } from "./contexts/AuthContext";
import { ThemeProvider } from "./contexts/ThemeContext";

const SUPABASE_PROJECT_URL = process.env.REACT_APP_SUPABASE_URL;
const ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY;
const REDIRECT_TO = process.env.REACT_APP_REDIRECT_TO ?? window.location.origin;

// Initialize the Supabase client
const supabase = createClient(SUPABASE_PROJECT_URL, ANON_KEY);

// Error boundary component
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="card p-8 m-4 text-red-400">
          <h2 className="text-xl font-semibold mb-4">Something went wrong.</h2>
          <pre className="text-sm opacity-80">{this.state.error.toString()}</pre>
        </div>
      );
    }
    return this.props.children;
  }
}

export default function App() {
  const [session, setSession] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Get initial session
    supabase.auth
      .getSession()
      .then(({ data: { session } }) => {
        setSession(session);
      })
      .catch((err) => {
        console.error("Error getting session:", err);
        setError(err);
      });

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
    });

    // Cleanup subscription
    return () => {
      if (subscription) subscription.unsubscribe();
    };
  }, []);

  if (error) {
    return (
      <div className="p-8">
        <div className="card">
          <p className="text-red-400">Error: {error.message}</p>
        </div>
      </div>
    );
  }

  if (!session) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="w-full max-w-[420px] p-5">
          <div className="card backdrop-blur-xl">
            <ErrorBoundary>
              <div className="text-center mb-6">
                <h1 className="text-3xl font-bold text-emerald-400 mb-2">WTF with Aisha</h1>
                <p className="text-gray-400 text-sm">Friends & Family Preview</p>
              </div>
              <Auth
                supabaseClient={supabase}
                appearance={{
                  theme: ThemeSupa,
                  variables: {
                    default: {
                      colors: {
                        brand: "rgb(6 95 70)", // emerald-800
                        brandAccent: "rgb(4 120 87)", // emerald-700
                        inputText: "rgb(229 231 235)", // text-gray-200
                      },
                    },
                  },
                  className: {
                    button: "glass-morphism !bg-emerald-800/30 hover:!bg-emerald-700/40",
                    input: "glass-morphism-dark text-gray-200",
                    label: "text-gray-200",
                  },
                }}
                providers={[]}
                redirectTo={REDIRECT_TO}
                showLinks={false}
              />
              <div className="mt-4 text-center">
                <a
                  href="mailto:gpsandhu23@gmail.com?subject=Request access to WTF with Aisha webapp"
                  className="glass-morphism !bg-emerald-800/30 hover:!bg-emerald-700/40 px-4 py-2 rounded-lg inline-block text-gray-200"
                >
                  Request Access
                </a>
              </div>
            </ErrorBoundary>
          </div>
        </div>
      </div>
    );
  }

  return (
    <ThemeProvider>
      <AuthProvider supabase={supabase}>
        <ChatProvider>
          <div className="min-h-screen">
            <Chat />
          </div>
        </ChatProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}
